import { useEffect, useRef } from 'react';

/**
 * Marks an unread notification read once it has been visibly on screen. Fires
 * `onView` a single time when the element crosses the viewport threshold and
 * stays long enough to count as "seen", then disconnects. No-ops when already
 * read so read cards never re-trigger the mutation.
 */
export const useMarkReadOnView = ({
  isRead,
  onView,
  dwellMs = 800,
  threshold = 0.6,
}: {
  isRead: boolean;
  onView: () => void;
  dwellMs?: number;
  threshold?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  // Hold onView in a ref so callers can pass a fresh arrow each render without
  // tearing down and re-observing (which would discard an in-flight dwell
  // timer and restart it from zero).
  const onViewRef = useRef(onView);
  useEffect(() => {
    onViewRef.current = onView;
  }, [onView]);

  useEffect(() => {
    const element = ref.current;
    if (isRead || hasFiredRef.current || !element) return;

    if (typeof IntersectionObserver === 'undefined') return;

    let dwellTimer: ReturnType<typeof setTimeout> | undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Clear any prior timer so overlapping intersecting entries (resize,
          // threshold re-cross) can't leave two live timers running.
          if (dwellTimer) clearTimeout(dwellTimer);
          dwellTimer = setTimeout(() => {
            // Re-check in case a second entry raced past the guard above.
            if (hasFiredRef.current) return;
            hasFiredRef.current = true;
            onViewRef.current();
            observer.disconnect();
          }, dwellMs);
        } else if (dwellTimer) {
          clearTimeout(dwellTimer);
        }
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      if (dwellTimer) clearTimeout(dwellTimer);
      observer.disconnect();
    };
  }, [isRead, dwellMs, threshold]);

  return ref;
};
